import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Ticket, UserRoundCheck } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { afterAuthPath } from '../../utils/afterAuth';
import SectionEyebrow from '../common/SectionEyebrow';
import IconTile from '../common/IconTile';
import Checkbox from '../common/Checkbox';
import Modal from '../common/Modal';
import { FormField } from '../common/FormField';
import PrivacyNotice from './PrivacyNotice';
import Footer from '../Footer';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/FormCard.css';
import '../../assets/styles/PinPage.css';

const API = process.env.REACT_APP_API_URL;
export const GOOGLE_SIGNUP_KEY = 'googleSignup';

const readPending = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GOOGLE_SIGNUP_KEY) || 'null');
  } catch (error) {
    return null;
  }
};

// Ultimo paso al entrar con Google por primera vez: la cuenta no se crea hasta aceptar
// la privacidad, igual que en el registro normal. Aqui tambien se puede indicar que es
// una ONG o meter un codigo de descuento.
const GoogleSignup = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [pending] = useState(readPending);
  const [privacy, setPrivacy] = useState(false);
  const [isOng, setIsOng] = useState(false);
  const [hasCode, setHasCode] = useState(false);
  const [code, setCode] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  if (!pending || !pending.signupToken) return <Navigate to="/login" replace />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;
    if (!privacy) {
      setErrors({ privacy: t('google_signup_privacy_required') });
      return;
    }
    setSending(true);
    setErrors({});

    try {
      const res = await fetch(`${API}/auth/google/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signupToken: pending.signupToken,
          acceptPrivacy: true,
          isONG: isOng,
          discountCode: hasCode ? code.trim() : ''
        })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 401) {
          sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);
          setErrors({ general: t('google_signup_expired') });
        } else if (data.field === 'discountCode') {
          setErrors({ code: data.message || t('google_signup_code_invalid') });
        } else {
          setErrors({ general: data.message || t('google_signup_error') });
        }
        setSending(false);
        return;
      }

      sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      window.dispatchEvent(new Event('authChange'));
      navigate(isOng ? '/ong-verification' : afterAuthPath('/choose'));
    } catch (error) {
      console.error('Error al crear la cuenta con Google:', error);
      setErrors({ general: t('google_signup_error') });
      setSending(false);
    }
  };

  return (
    <div className="brand-page-stack pin-page">
      <section className="section-band">
        <div className="section-band__inner tp-pin">
          <div className="tp-pin__main">
            <h1 className="tp-sr-only">{t('google_signup_title')}</h1>
            <div aria-hidden="true">
              <SectionEyebrow>{t('google_signup_title')}</SectionEyebrow>
            </div>
            <p className="section-band__lead tp-pin__lead">
              {t('google_signup_lead')
                .replace('{name}', pending.name || pending.email)
                .replace('{email}', pending.email)}
            </p>

            <form className="tp-form tp-pin__form" onSubmit={handleSubmit} noValidate>
              <div className="tp-form__checks">
                <Checkbox
                  checked={privacy}
                  onChange={() => { setPrivacy(!privacy); setErrors({}); }}
                  aria-invalid={errors.privacy ? true : undefined}
                >
                  {t('signup_privacy_accept')}{' '}
                  <button type="button" className="tp-check__link" onClick={() => setShowPrivacy(true)}>
                    {t('signup_privacy_policy')}
                  </button>
                </Checkbox>
                {errors.privacy && <p className="tp-field__error" role="alert">{errors.privacy}</p>}

                <Checkbox checked={isOng} onChange={() => setIsOng(!isOng)}>
                  {t('signup_is_ong')}
                </Checkbox>
                {isOng && <p className="tp-field__hint">{t('google_signup_ong_hint')}</p>}

                <Checkbox
                  checked={hasCode}
                  onChange={() => { setHasCode(!hasCode); setErrors({}); }}
                >
                  {t('signup_discount_toggle')}
                </Checkbox>
                {hasCode && (
                  <FormField
                    id="google-signup-code"
                    label={t('signup_discount_placeholder')}
                    hideLabel
                    icon={<Ticket />}
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setErrors({}); }}
                    placeholder={t('signup_discount_placeholder')}
                    error={errors.code}
                  />
                )}
              </div>

              {errors.general && <p className="tp-form__alert" role="alert">{errors.general}</p>}

              <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
                {t(sending ? 'google_signup_creating' : 'google_signup_submit')}
                <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
              <p className="tp-form__footer">
                <Link
                  to="/login"
                  className="tp-form__link"
                  onClick={() => sessionStorage.removeItem(GOOGLE_SIGNUP_KEY)}
                >
                  {t('google_signup_other_account')}
                </Link>
              </p>
            </form>
          </div>

          <div className="tp-pin__media">
            <IconTile accent="#7C3AED" icon={<UserRoundCheck size="1em" strokeWidth={1.25} />} />
          </div>
        </div>
      </section>

      <Modal
        open={showPrivacy}
        onOpenChange={setShowPrivacy}
        size="lg"
        icon={<ShieldCheck size={18} strokeWidth={2} />}
        tone="#7C3AED"
        title={t('signup_privacy_modal_title')}
        cancelText={t('signup_ong_close')}
        confirmText={t('signup_privacy_accept_btn')}
        onConfirm={() => { setPrivacy(true); setErrors({}); }}
      >
        <PrivacyNotice />
      </Modal>
      <Footer />
    </div>
  );
};

export default GoogleSignup;
